import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Building2, Users, ShoppingBag, Mail, DollarSign, FileText, Activity as ActivityIcon, AlertTriangle } from "lucide-react";

const KpiCard = ({ label, value, sub, icon: Icon, to, testId }) => (
  <Link to={to || "#"} data-testid={testId} className="block border border-border bg-card p-6 hover:border-accent transition-colors group">
    <div className="flex items-start justify-between">
      <div>
        <div className="overline text-muted-foreground">{label}</div>
        <div className="font-display text-4xl mt-3 tracking-tight" data-testid={`${testId}-value`}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
      </div>
      <div className="w-10 h-10 border border-border flex items-center justify-center bg-background group-hover:bg-accent group-hover:text-accent-foreground group-hover:border-accent transition-colors">
        <Icon size={16} />
      </div>
    </div>
  </Link>
);

const formatUSD = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [comingSoon, setComingSoon] = useState(null);
  const [bestSellers, setBestSellers] = useState([]);
  const [topViewed, setTopViewed] = useState([]);
  const [resendQuota, setResendQuota] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, cs, bs, tv] = await Promise.all([
          api.get("/admin/stats/overview"),
          api.get("/admin/stats/coming-soon"),
          api.get("/admin/stats/best-sellers?limit=5"),
          api.get("/admin/stats/top-plans?metric=views&limit=5"),
        ]);
        if (!alive) return;
        setStats(s.data);
        setComingSoon(cs.data);
        setBestSellers(bs.data);
        setTopViewed(tv.data);
        // Best-effort quota probe (super_admin only endpoint — non-admins get 403 and we simply skip)
        try {
          const q = await api.get("/admin/resend-quota");
          if (alive) setResendQuota(q.data);
        } catch (e) { /* non-super_admin — silently skip */ if (typeof console !== "undefined") console.debug("[silent]", e); }
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || "Failed to load dashboard");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <AdminLayout title="Dashboard">
      {loading && <div className="text-muted-foreground" data-testid="dashboard-loading">Loading dashboard…</div>}
      {err && <div className="border border-destructive/30 bg-destructive/5 text-destructive p-4 text-sm" data-testid="dashboard-error">{err}</div>}
      {stats && (
        <div className="space-y-8" data-testid="dashboard-content">
          {stats.email_health && !stats.email_health.configured && (
            <div
              className="border-l-4 border-l-[#C85A32] border border-border bg-[#FFF6F0] p-4 sm:p-5 flex items-start gap-4"
              data-testid="email-delivery-banner"
            >
              <div className="w-9 h-9 shrink-0 flex items-center justify-center bg-[#C85A32] text-white rounded-full">
                <AlertTriangle size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#1B1B1B] mb-1" data-testid="email-banner-title">
                  Email delivery is inactive — enquiries are being saved but not emailed
                </div>
                <p className="text-sm text-[#555] leading-relaxed">
                  No Resend API key is configured, so contact-form and custom-design notifications
                  aren&apos;t being sent to <b>{stats.email_health.notification_email_primary || "the primary recipient"}</b>
                  {stats.email_health.notification_email_cc?.length > 0 && (
                    <> or <b>{stats.email_health.notification_email_cc.join(", ")}</b></>
                  )}.
                  {stats.email_health.undelivered_enquiries > 0 && (
                    <> <b data-testid="email-banner-undelivered-count">
                      {stats.email_health.undelivered_enquiries} open enquir{stats.email_health.undelivered_enquiries === 1 ? "y" : "ies"}
                    </b> {stats.email_health.undelivered_enquiries === 1 ? "is" : "are"} waiting for review in the admin dashboard.</>
                  )}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to="/admin/enquiries"
                    data-testid="email-banner-view-enquiries"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#2C4C3B] text-white text-xs font-medium hover:bg-[#1e3628] transition-colors"
                  >
                    <Mail size={14} /> View all enquiries
                  </Link>
                  <Link
                    to="/admin/settings?section=integrations"
                    data-testid="email-banner-configure"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-[#2C4C3B] text-[#2C4C3B] text-xs font-medium hover:bg-[#2C4C3B]/5 transition-colors"
                  >
                    Configure Resend
                  </Link>
                </div>
              </div>
            </div>
          )}
          {resendQuota?.probe?.daily_quota && Number(resendQuota.probe.daily_quota) >= 80 && (
            <div
              className={`border-l-4 ${Number(resendQuota.probe.daily_quota) >= 100 ? "border-l-[#C85A32] bg-[#FFF0E8]" : "border-l-[#D4A017] bg-[#FFF9E6]"} border border-border p-4 sm:p-5 flex items-start gap-4`}
              data-testid="resend-quota-banner"
            >
              <div className={`w-9 h-9 shrink-0 flex items-center justify-center ${Number(resendQuota.probe.daily_quota) >= 100 ? "bg-[#C85A32]" : "bg-[#D4A017]"} text-white rounded-full`}>
                <AlertTriangle size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#1B1B1B] mb-1" data-testid="resend-quota-title">
                  {Number(resendQuota.probe.daily_quota) >= 100
                    ? `Resend daily quota exceeded (${resendQuota.probe.daily_quota}/100 free tier)`
                    : `Resend quota approaching limit (${resendQuota.probe.daily_quota}/100)`}
                </div>
                <p className="text-sm text-[#555] leading-relaxed">
                  Emails may be rejected or delayed until quota resets at 00:00 UTC. Upgrade the Resend plan to Pro (50k/month) to remove the daily cap.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    href="https://resend.com/settings/billing"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="resend-quota-upgrade"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#2C4C3B] text-white text-xs font-medium hover:bg-[#1e3628] transition-colors"
                  >
                    Upgrade Resend plan
                  </a>
                  <a
                    href="https://resend.com/emails"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="resend-quota-view-logs"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-[#2C4C3B] text-[#2C4C3B] text-xs font-medium hover:bg-[#2C4C3B]/5 transition-colors"
                  >
                    View Resend logs
                  </a>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiCard testId="kpi-revenue" label="Total revenue" value={formatUSD(stats.orders.revenue_usd)} sub={`${stats.orders.total} orders`} icon={DollarSign} to="/admin/orders" />
            <KpiCard testId="kpi-plans" label="Published plans" value={stats.plans.published} sub={`${stats.plans.draft} draft · ${stats.plans.archived} archived`} icon={Building2} to="/admin/plans" />
            <KpiCard testId="kpi-customers" label="Customers" value={stats.users.customers} sub={`${stats.users.staff} staff`} icon={Users} to="/admin/customers" />
            <KpiCard testId="kpi-enquiries" label="Open enquiries" value={stats.enquiries.open} sub={`${stats.enquiries.new_30d} new · 30 days`} icon={Mail} to="/admin/enquiries" />
          </div>

          {(comingSoon || bestSellers.length > 0 || topViewed.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="border border-border bg-card" data-testid="dashboard-coming-soon">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <div className="overline text-accent">Coming Soon buzz</div>
                  <Link to="/coming-soon" className="text-xs text-muted-foreground hover:text-accent">View →</Link>
                </div>
                {comingSoon && comingSoon.plans.length === 0 && <div className="p-6 text-sm text-muted-foreground">No coming-soon plans right now.</div>}
                {comingSoon && comingSoon.plans.length > 0 && (
                  <div className="divide-y divide-border">
                    <div className="px-6 py-3 text-xs text-muted-foreground bg-background">
                      <span className="font-medium text-foreground">{comingSoon.total}</span> total notify-me subscribers across {comingSoon.plans.length} drafts
                    </div>
                    {comingSoon.plans.slice(0, 5).map((p) => (
                      <Link key={p.id} to={`/admin/plans/${p.id}/edit`} data-testid={`cs-plan-${p.id}`} className="flex items-center justify-between p-4 hover:bg-background">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{p.title}</div>
                          <div className="text-[11px] text-muted-foreground">{p.launch_date ? new Date(p.launch_date).toLocaleDateString() : "No launch date"}</div>
                        </div>
                        <div className="text-lg font-display text-accent">{p.subscribers}</div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div className="border border-border bg-card" data-testid="dashboard-best-sellers">
                <div className="px-6 py-4 border-b border-border overline text-accent">Best sellers</div>
                {bestSellers.length === 0 && <div className="p-6 text-sm text-muted-foreground">No sales yet.</div>}
                <div className="divide-y divide-border">
                  {bestSellers.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-4">
                      <div className="text-sm truncate min-w-0"><div className="truncate">{p.title}</div><div className="text-xs text-muted-foreground">{p.style}</div></div>
                      <div className="text-lg font-display text-accent">{p.purchases}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-border bg-card" data-testid="dashboard-top-viewed">
                <div className="px-6 py-4 border-b border-border overline text-accent">Most viewed</div>
                {topViewed.length === 0 && <div className="p-6 text-sm text-muted-foreground">No views recorded yet.</div>}
                <div className="divide-y divide-border">
                  {topViewed.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-4">
                      <div className="text-sm truncate min-w-0"><div className="truncate">{p.title}</div><div className="text-xs text-muted-foreground">{p.style}</div></div>
                      <div className="text-lg font-display text-accent">{p.views}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 border border-border bg-card">
              <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                <div className="overline text-accent">Recent enquiries</div>
                <Link to="/admin/enquiries" className="text-xs text-muted-foreground hover:text-accent" data-testid="view-all-enquiries">View all →</Link>
              </div>
              <div className="divide-y divide-border">
                {stats.recent_enquiries.length === 0 && (
                  <div className="p-6 text-sm text-muted-foreground">No enquiries yet.</div>
                )}
                {stats.recent_enquiries.map((e) => (
                  <Link
                    key={e.id}
                    to={`/admin/enquiries?open=${e.id}`}
                    data-testid={`recent-enquiry-${e.id}`}
                    className="flex items-start justify-between gap-4 p-4 hover:bg-background transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{e.subject || `${e.type} enquiry`}</div>
                      <div className="text-xs text-muted-foreground mt-1 truncate">{e.name} · {e.email}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-accent whitespace-nowrap">{e.status || "new"}</div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="border border-border bg-card">
              <div className="px-6 py-4 border-b border-border flex items-center gap-2">
                <ActivityIcon size={14} className="text-accent" />
                <div className="overline text-accent">Recent activity</div>
              </div>
              <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
                {stats.recent_activity.length === 0 && (
                  <div className="p-6 text-sm text-muted-foreground">No recent activity.</div>
                )}
                {stats.recent_activity.map((a) => (
                  <div key={a.id || a.timestamp} className="p-4 text-xs" data-testid="recent-activity-row">
                    <div className="font-medium">{a.action}</div>
                    <div className="text-muted-foreground mt-1">
                      {a.user_name || a.user_id} · {new Date(a.timestamp).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/admin/plans/new" data-testid="quick-add-plan" className="border border-border bg-card p-6 hover:border-accent transition-colors">
              <FileText size={18} className="text-accent mb-3" />
              <div className="font-medium">Add new plan</div>
              <div className="text-xs text-muted-foreground mt-1">Publish a new house plan or save as draft</div>
            </Link>
            <Link to="/admin/enquiries" data-testid="quick-enquiries" className="border border-border bg-card p-6 hover:border-accent transition-colors">
              <Mail size={18} className="text-accent mb-3" />
              <div className="font-medium">Manage enquiries</div>
              <div className="text-xs text-muted-foreground mt-1">Reply to contact & custom design requests</div>
            </Link>
            <Link to="/admin/orders" data-testid="quick-orders" className="border border-border bg-card p-6 hover:border-accent transition-colors">
              <ShoppingBag size={18} className="text-accent mb-3" />
              <div className="font-medium">Review orders</div>
              <div className="text-xs text-muted-foreground mt-1">See payments, refunds and downloads</div>
            </Link>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
